import React, { useReducer, useState, useRef, useEffect } from 'react';
import styles from './ThreadPage.module.css';
import Message from './Message';
import BotReply from './BotReply';
import { threadHistory, currentUser } from '../mocks/thread';
import { selectQuestions } from '../mocks/questions';
import { summarizeThread } from '../lib/summarizer';
import { generatePrd, prdToMarkdown } from '../lib/prdGenerator';
import { downloadMarkdown } from '../lib/fileDownload';
import type { BotState, ThreadMessage as ThreadMessageType } from '../types';

type BotAction =
  | { type: 'MENTION' }
  | { type: 'ANSWER'; id: string; value: string }
  | { type: 'APPROVE' }
  | { type: 'REVISION'; note: string }
  | { type: 'RESET' };

const initialBotState: BotState = {
  mentioned: false,
  featureSummary: '',
  questions: [],
  userAnswers: {},
  approved: false,
  prdMarkdown: '',
};

function botReducer(state: BotState, action: BotAction): BotState {
  switch (action.type) {
    case 'MENTION': {
      const text = threadHistory.map((m) => m.text).join(' ');
      const summary = summarizeThread(text);
      const questions = selectQuestions(text);
      const userAnswers: Record<string, string> = {};
      questions.forEach((q) => {
        userAnswers[q.id] = q.defaultAnswer;
      });
      return {
        ...state,
        mentioned: true,
        featureSummary: summary,
        questions,
        userAnswers,
        approved: false,
        prdMarkdown: '',
        revisionNote: undefined,
      };
    }
    case 'ANSWER':
      return {
        ...state,
        userAnswers: { ...state.userAnswers, [action.id]: action.value },
      };
    case 'APPROVE': {
      const markdown = prdToMarkdown(
        generatePrd(state.featureSummary, state.questions, state.userAnswers)
      );
      downloadMarkdown('prd.md', markdown);
      return { ...state, approved: true, prdMarkdown: markdown, revisionNote: undefined };
    }
    case 'REVISION': {
      const markdown = prdToMarkdown(
        generatePrd(state.featureSummary, state.questions, state.userAnswers, action.note)
      );
      downloadMarkdown('prd.md', markdown);
      return { ...state, approved: true, prdMarkdown: markdown, revisionNote: action.note };
    }
    case 'RESET':
      return initialBotState;
    default:
      return state;
  }
}

function botMessageFromState(state: BotState): ThreadMessageType {
  return {
    id: 'bot',
    userName: 'prd-bot',
    avatarUrl: 'https://ui-avatars.com/api/?name=PRD+Bot&background=36c5f0&color=fff',
    timestamp: new Date().toISOString(),
    text: state.approved
      ? 'I generated a PRD based on our discussion. See the summary below.'
      : 'I can help turn this thread into a PRD. Here are a few clarifying questions.',
  };
}

export default function ThreadPage() {
  const [botState, dispatch] = useReducer(botReducer, initialBotState);
  const [draft, setDraft] = useState('');
  const [userMessages, setUserMessages] = useState<ThreadMessageType[]>([]);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [userMessages, botState.mentioned, botState.approved]);

  useEffect(() => {
    if (botState.prdMarkdown) {
      localStorage.setItem('prd-bot-markdown', botState.prdMarkdown);
    }
  }, [botState.prdMarkdown]);

  const handleSend = () => {
    const text = draft.trim();
    if (!text) return;

    const newMessage: ThreadMessageType = {
      id: `u-${Date.now()}`,
      userName: currentUser.userName,
      avatarUrl: currentUser.avatarUrl,
      timestamp: new Date().toISOString(),
      text,
    };
    setUserMessages((prev) => [...prev, newMessage]);
    setDraft('');

    const lower = text.toLowerCase();

    if (lower.includes('@prd-bot') && !botState.mentioned) {
      dispatch({ type: 'MENTION' });
    } else if (lower.includes('approved') && botState.mentioned && !botState.approved) {
      dispatch({ type: 'APPROVE' });
    } else if (botState.approved && !lower.includes('approved')) {
      dispatch({ type: 'REVISION', note: text });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const allMessages = [...threadHistory, ...userMessages];

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1># product-requirements</h1>
        <span className={styles.threadMeta}>Thread prototype • {allMessages.length + (botState.mentioned ? 1 : 0)} messages</span>
      </header>

      <div className={styles.messages}>
        <div className={styles.dayDivider}>Today</div>
        {allMessages.map((msg) => (
          <Message key={msg.id} message={msg} />
        ))}
        {botState.mentioned ? (
          <Message message={botMessageFromState(botState)} isBot>
            <BotReply
              summary={botState.featureSummary}
              questions={botState.questions}
              userAnswers={botState.userAnswers}
              approved={botState.approved}
              revisionNote={botState.revisionNote}
              onAnswerChange={(id, value) => dispatch({ type: 'ANSWER', id, value })}
            />
          </Message>
        ) : null}
        <div ref={messagesEndRef} />
      </div>

      <div className={styles.composer}>
        <div className={styles.inputWrap}>
          <textarea
            data-testid="mention-input"
            className={styles.input}
            placeholder="Message #product-requirements (try @prd-bot or approved)"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={2}
          />
          <div className={styles.toolbar}>
            <span className={styles.help}>
              {botState.approved
                ? 'Send a revision note to update the PRD'
                : botState.mentioned
                ? 'Edit defaults above, then reply approved'
                : 'Mention @prd-bot to start'}
            </span>
            <button
              className={styles.sendButton}
              onClick={handleSend}
              disabled={!draft.trim()}
            >
              Send
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
